import React, { useMemo } from 'react';
import { xcss, Box } from '@atlaskit/primitives';
import AppBaseDI from '@atlassian/jira-app-base';
import { createProjectUrl } from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/common/create-url';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { ServiceDeskAnalyticsContext } from '@atlassian/jira-servicedesk-analytics/src/ui/servicedesk-analytics-context/index.tsx';
import { ServiceDeskErrorBoundary } from '@atlassian/jira-servicedesk-common/src/ui/components/servicedesk-error-boundary';
import { JsdGettingStartedPanelShouldRender } from '@atlassian/jira-servicedesk-getting-started-panel/src/async';
import { ServiceDeskDocumentTitle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/document-title/index.tsx';
import { GenericError as ErrorViewDI } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/errors';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { PageStyle as PageStyleDI } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-style/index.tsx';
import { SpaPageWrapper } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-wrapper/index.tsx';
import {
	Header as CommonNav4Header,
	type Breadcrumb,
} from '@atlassian/jsm-enterprise-reports-common';
import { useRouter } from '@atlassian/react-resource-router';
import ReportsBreadcrumb from './breadcrumb/main.tsx';
import Header from './header';
import messages from './messages';
import type { Props } from './types';

const ReportsAppBase = ({
	appName,
	pageId,
	pageTitle,
	children,
	AppBase,
	ErrorView,
	PageStyle,
	actions,
	dontShowHeader = false,
}: Props) => {
	const [{ match }] = useRouter();
	const [{ data: pageContextData, error }] = usePageContext();
	const { tenantContext, projectContext, serviceDeskContext } = pageContextData || {};
	const { projectKey, projectName } = projectContext || {};
	const { baseUrl } = tenantContext || {};
	const { formatMessage } = useIntl();

	const pageContextProps = useMemo(
		() => ({ ...tenantContext, ...projectContext, ...serviceDeskContext }),
		[projectContext, serviceDeskContext, tenantContext],
	);

	const breadcrumbs: Breadcrumb[] = [
		{ href: '/jira/projects', text: formatMessage(messages.projectsBreadcrumb) },
		{ href: createProjectUrl(projectKey || ''), text: projectName || '' },
		{
			href: `${baseUrl}/jira/servicedesk/projects/${projectKey}/reports`,
			text: formatMessage(messages.reportsBreadcrumb),
		},
	];

	return (
		<>
			<SpaPageWrapper
				pageId={pageId}
				baseUrl={baseUrl}
				currentUrl={match.url}
				// @ts-expect-error - TS2322 - Type '{ projectId: number; projectKey: string; projectName: string; projectType: ProjectType; isAdmin: boolean; isProjectSimplified: boolean; } | undefined' is not assignable to type 'ProjectContext | null | undefined'.
				projectContext={projectContext}
				isLoading={!pageContextData && !error}
				error={error}
			>
				{/* @ts-expect-error - TS2322 - Type '{ projectName: string | undefined; section: string; }' is not assignable to type 'IntrinsicAttributes & Diff<(Diff<RefAttributes<any> | (RefAttributes<any> & { children?: ReactNode; }), Partial<RefAttributes<...> | (RefAttributes<...> & { ...; })>> & RefAttributes<...>) | (Diff<...> & ... 1 more ... & { ...; }), InjectIntlVoidProps> & { ...; }'. */}
				<ServiceDeskDocumentTitle projectName={projectName} section="reports" />
				<AppBase id={pageId}>
					<ServiceDeskAnalyticsContext>
						<ServiceDeskErrorBoundary appName={appName} ErrorView={ErrorView}>
							{!dontShowHeader &&
								(getWillShowNav4() ? (
									<>
										<CommonNav4Header
											title={pageTitle ? formatMessage(pageTitle) : ''}
											breadcrumbs={breadcrumbs}
											actions={actions}
										/>
									</>
								) : (
									<Box xcss={HeaderContainer}>
										<PageStyle>
											<Header
												breadcrumbs={
													<ReportsBreadcrumb
														// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
														baseUrl={baseUrl}
														// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
														projectKey={projectKey}
														// @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
														projectName={projectName}
													/>
												}
												title={pageTitle}
												actions={actions}
											/>
										</PageStyle>
									</Box>
								))}
							<PageStyle>
								{children({
									pageContext: pageContextProps,
								})}
							</PageStyle>
						</ServiceDeskErrorBoundary>
					</ServiceDeskAnalyticsContext>
				</AppBase>
			</SpaPageWrapper>
			<JsdGettingStartedPanelShouldRender />
		</>
	);
};

const HeaderContainer = xcss({
	paddingBottom: 'space.200',
});

ReportsAppBase.defaultProps = {
	AppBase: AppBaseDI,
	ErrorView: ErrorViewDI,
	PageStyle: PageStyleDI,
};
export default ReportsAppBase;
